import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Mic, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { interviewsAPI } from '@/lib/interviewAPI';
import api from '@/lib/api';
import { safeFormat } from '@/lib/utils';

export const STATUS_LABELS = {
  draft: 'Draft', researching: 'Researching', ready: 'Brief ready', invited: 'Invited', interviewing: 'Interviewing',
  interviewed: 'Interviewed', follow_up: 'Follow-ups', compiling: 'Drafting', review: 'In review',
  approved: 'Approved', delivered: 'Delivered',
};
export const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', researching: 'bg-amber-50 text-amber-800', ready: 'bg-sky-50 text-sky-800',
  invited: 'bg-sky-50 text-sky-800', interviewing: 'bg-violet-50 text-violet-800', interviewed: 'bg-violet-50 text-violet-800',
  follow_up: 'bg-amber-50 text-amber-800', compiling: 'bg-amber-50 text-amber-800', review: 'bg-orange-50 text-orange-800',
  approved: 'bg-emerald-50 text-emerald-800', delivered: 'bg-emerald-100 text-emerald-900',
};
export const SUBJECT_TYPES = [
  { id: 'music_artist', label: 'Music artist / DJ' },
  { id: 'speaker', label: 'Professional speaker' },
  { id: 'professional', label: 'Professional / creator' },
];

const LINK_HINTS = {
  music_artist: ['Beatport artist page', 'SoundCloud', 'Spotify artist', 'YouTube channel', 'Instagram', 'Website / Bandcamp'],
  speaker: ['Website', 'YouTube channel', 'LinkedIn', 'Speaker bureau page', 'Instagram'],
  professional: ['Website', 'LinkedIn', 'YouTube channel', 'Instagram'],
};

// Link the interview to the artist's record in the AMR portal (releases, tracks, press kit on file)
export const AmrPicker = ({ value, valueName, onPick }) => {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [msg, setMsg] = useState('');
  const search = async () => {
    if (!q.trim()) return;
    setMsg('');
    try {
      const r = await interviewsAPI.amrSearch(q.trim());
      setRes(r.data);
      if (!r.data.length) setMsg('No artist found in the AMR portal.');
    } catch (e) { setRes(null); setMsg(e?.response?.data?.detail || 'Could not search the AMR portal.'); }
  };
  return (
    <div>
      {value ? (
        <div className="flex items-center gap-2 text-sm"><span className="rounded-full bg-[#F3EFE7] px-3 py-1">Linked: {valueName || value}</span>
          <button type="button" className="text-xs text-[#A62B1F] font-semibold" onClick={() => onPick(null)}>Unlink</button></div>
      ) : (
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the AMR portal by name or email"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }} />
          <Button type="button" variant="outline" onClick={search}>Search</Button>
        </div>
      )}
      {!value && res && res.length > 0 && (
        <div className="mt-2 rounded-lg border border-[#E3DDD1] divide-y divide-[#E3DDD1]">
          {res.map((a) => (
            <button type="button" key={a.id} onClick={() => { onPick(a); setRes(null); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#FBF8F2]">
              <span className="font-semibold">{a.name}</span>{a.email && <span className="text-[#5C6670]"> · {a.email}</span>}
            </button>
          ))}
        </div>
      )}
      {msg && <p className="text-xs text-[#5C6670] mt-1">{msg}</p>}
    </div>
  );
};

// Merge links into a list, skipping ones already there (ignores http/www/trailing slash)
export const mergeLinks = (existing, incoming) => {
  const norm = (u) => u.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^(www|m)\./, '').replace(/\/$/, '');
  const filled = existing.filter((u) => u.trim());
  const have = new Set(filled.map(norm));
  const add = (incoming || []).filter((u) => u && !have.has(norm(u)));
  const merged = [...filled, ...add];
  return merged.length < 3 ? [...merged, ...Array(3 - merged.length).fill('')] : [...merged, ''];
};

const blank = () => ({
  subject_type: 'music_artist', name: '', email: '', phone: '', hometown: '', notes: '', sms_opt_in: false, amr: null,
  links: ['', '', '', '', '', ''],
});

export default function Interviews() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  // AMR artist search is AMR's own portal data — only shown to companies it's switched on for
  const [amrOn, setAmrOn] = useState(false);
  useEffect(() => {
    api.get('/api/auth/me').then((r) => setAmrOn(!!r.data?.company?.amr_portal_enabled)).catch(() => setAmrOn(false));
  }, []);

  const load = () => interviewsAPI.list().then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error('Add a name');
    setSaving(true);
    try {
      const r = await interviewsAPI.create({
        subject_type: form.subject_type,
        subject: { name: form.name.trim(), email: form.email || null, phone: form.phone || null, hometown: form.hometown || null },
        links: form.links.filter((l) => l.trim()).map((url) => ({ url: url.trim() })),
        notes: form.notes || null,
        sms_opt_in: form.sms_opt_in,
        amr_artist_id: form.amr?.id || null,
      });
      try { await interviewsAPI.research(r.data.id); } catch (e) { /* research can be started from the page */ }
      toast.success('Created. Research has started.');
      navigate(`/interviews/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create');
    } finally { setSaving(false); }
  };

  const remove = async (e, row) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the interview for ${row.subject?.name}? This removes the recording and package.`)) return;
    await interviewsAPI.remove(row.id);
    load();
  };

  const filtered = (rows || []).filter((r) => !q || (r.subject?.name || '').toLowerCase().includes(q.toLowerCase()));
  const hints = LINK_HINTS[form.subject_type];

  return (
    <Layout title="Interviews">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E2A33]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Stagehead Interview</h1>
          <p className="text-sm text-[#5C6670]">Research, interview and press kit for each person, in one place.</p>
        </div>
        <Button onClick={() => { setForm(blank()); setOpen(true); }} className="bg-[#A62B1F] hover:bg-[#8C2318] text-white">
          <Plus className="w-4 h-4 mr-1" />New interview
        </Button>
      </div>

      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6670]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="pl-9" />
      </div>

      <div className="rounded-xl border border-[#E3DDD1] bg-white overflow-hidden">
        {rows === null ? (
          <p className="p-6 text-sm text-[#5C6670]">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Mic className="w-8 h-8 mx-auto text-[#A62B1F] mb-2" />
            <p className="font-semibold text-[#1E2A33]">No interviews yet</p>
            <p className="text-sm text-[#5C6670]">Start with a name and their links. Research runs before anyone is contacted.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FBF8F2] text-[#5C6670] text-left">
              <tr><th className="px-4 py-2.5 font-medium">Name</th><th className="px-4 py-2.5 font-medium hidden md:table-cell">Type</th>
                <th className="px-4 py-2.5 font-medium">Status</th><th className="px-4 py-2.5 font-medium hidden md:table-cell">Requested by</th>
                <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Updated</th><th /></tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/interviews/${r.id}`)} className="border-t border-[#E3DDD1] hover:bg-[#FBF8F2] cursor-pointer">
                  <td className="px-4 py-3 font-semibold text-[#1E2A33]">{r.subject?.name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#5C6670]">{SUBJECT_TYPES.find((t) => t.id === r.subject_type)?.label}</td>
                  <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[r.status] || ''} border-0`}>{STATUS_LABELS[r.status] || r.status}</Badge></td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#5C6670]">{r.requested_by?.name}</td>
                  <td className="px-4 py-3 hidden sm:table-cell text-[#5C6670]">{safeFormat(r.updated_at, 'MMM d')}</td>
                  <td className="px-2 py-3 text-right"><button onClick={(e) => remove(e, r)} className="p-1.5 text-[#5C6670] hover:text-[#A62B1F]" title="Delete"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New interview</DialogTitle>
            <DialogDescription>Add who it's for and where they live online. Research starts right away; nobody is contacted until you send the invite.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              {SUBJECT_TYPES.map((t) => (
                <button key={t.id} type="button" onClick={() => setForm((f) => ({ ...f, subject_type: t.id }))}
                  className={`px-3 py-1.5 rounded-full text-sm border ${form.subject_type === t.id ? 'bg-[#1E2A33] text-white border-[#1E2A33]' : 'bg-white text-[#5C6670] border-[#E3DDD1]'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            {amrOn && form.subject_type === 'music_artist' && (
              <div><Label>AMR artist (optional)</Label>
                <div className="mt-1"><AmrPicker value={form.amr?.id} valueName={form.amr?.name}
                  onPick={async (a) => {
                    setForm((f) => ({ ...f, amr: a, name: f.name || a?.name || '', email: f.email || a?.email || '' }));
                    if (!a) return;
                    try {
                      const { data: p } = await interviewsAPI.amrArtist(a.id);
                      setForm((f) => ({
                        ...f,
                        name: f.name || p.name || '', email: f.email || p.email || '', phone: f.phone || p.phone || '',
                        hometown: f.hometown || p.hometown || '', links: mergeLinks(f.links, p.links),
                      }));
                      toast.success(p.links?.length ? `Filled in ${p.links.length} link${p.links.length > 1 ? 's' : ''} from AMR` : 'Linked. No profile links on file in AMR.');
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Linked, but could not load their AMR details'); }
                  }} /></div>
                <p className="text-xs text-[#5C6670] mt-1">Fills in their links and details from AMR, and pulls their releases, tracks and press kit into the research.</p>
              </div>
            )}
            <div><Label>Name (artist or professional name)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mobile</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="416 555 1234" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <label className="flex gap-2 text-sm text-[#1E2A33]">
              <input type="checkbox" checked={form.sms_opt_in} onChange={(e) => setForm({ ...form, sms_opt_in: e.target.checked })} className="mt-0.5" />
              They've agreed to receive texts about this (needed before we text them).
            </label>
            <div><Label>Hometown / base</Label><Input value={form.hometown} onChange={(e) => setForm({ ...form, hometown: e.target.value })} /></div>
            <div>
              <Label>Links</Label>
              <div className="space-y-2 mt-1">
                {form.links.map((l, i) => (
                  <Input key={i} value={l} placeholder={hints[i] || 'Another link'}
                    onChange={(e) => setForm((f) => ({ ...f, links: f.links.map((x, j) => (j === i ? e.target.value : x)) }))} />
                ))}
                <button type="button" className="text-xs font-semibold text-[#A62B1F]" onClick={() => setForm((f) => ({ ...f, links: [...f.links, ''] }))}>+ Add link</button>
              </div>
            </div>
            <div><Label>Notes for the research and interview</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything we know: upcoming release, what the kit is for, topics to avoid…" /></div>
            <Button onClick={create} disabled={saving} className="w-full bg-[#A62B1F] hover:bg-[#8C2318] text-white">{saving ? 'Creating…' : 'Create and start research'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
